<template>
  <div>
    <div class="d-flex align-items-center justify-content-between mb-3">
      <div />
      <button
        class="btn btn-sm btn-primary"
        @click="edited = null; form = true"
      >
        <i class="fas fa-plus pr-1" />
        {{ $t('general.add') }}
      </button>
    </div>
    <hr class="mt-1">
    <div class="subscription-scroll">
      <SubscriptionRow
        v-for="sub in parsedSubscriptions"
        :key="sub.id"
        :sub="sub"
        clickable
        @click.native="edited = sub; form = true"
      >
        <div
          class="ml-2"
          style="border-left: 1px solid #eee;"
          @click.stop="selectedReport = sub"
        >
          <div
            v-tippy
            class="secondary-icon ml-2"
            :class="{ disabled: pending[sub.id] }"
            :content="$t('shop.downloadReport')"
          >
            <Loader
              v-if="pending[sub.id] === 'download'"
              size="20px"
              style="padding-top: 6px"
            />
            <i
              v-else
              class="fa-solid fa-download"
            />
          </div>
        </div>
        <div
          @click.stop="sendReport(sub.id)"
        >
          <div
            v-tippy
            class="secondary-icon ml-2"
            :class="{ disabled: pending[sub.id] }"
            :content="$t('shop.reports.sendReport')"
          >
            <Loader
              v-if="pending[sub.id] === 'email'"
              size="20px"
              style="padding-top: 6px"
            />
            <i
              v-else
              class="fas fa-paper-plane"
            />
          </div>
        </div>
      </SubscriptionRow>
    </div>

    <BModal
      :visible="!!selectedReport"
      hide-footer
      hide-header
      size="lg"
      @hidden="selectedReport = null"
    >
      <SubscriptionRow
        :sub="selectedReport"
      />
      <div class="d-flex flex-column align-items-center">
        <div
          v-if="selectedReport?.reportType?.multiDay"
          class="text-center"
        >
          <div
            class="font-weight-bold mb-2"
            style="font-size: 1.2rem"
          >
            Przedział czasu:
          </div>
          <div style="width: 400px">
            <CtkDateTimePicker
              v-model="reportDates"
              format="YYYY-MM-DD"
              class="date-picker"
              formatted="DD MMM YYYY"
              only-date
              label="Zakres raportu"
              :locale="locale"
              range
              inline
              :custom-shortcuts="customShortcuts"
            />
          </div>
        </div>
        <div v-else>
          <div
            class="font-weight-bold mb-2"
            style="font-size: 1.2rem"
          >
            Dzień:
          </div>
          <div style="width: 400px">
            <CtkDateTimePicker
              v-model="reportDates.start"
              format="YYYY-MM-DD"
              class="date-picker"
              formatted="DD MMM YYYY"
              only-date
              label="Zakres raportu"
              :locale="locale"
              inline
            />
          </div>
        </div>
        <hr>
        <div
          v-if="selectedReport"
          class="mt-3"
        >
          <button
            class="btn btn-sm btn-primary"
            @click="downloadReport(selectedReport.id, false)"
          >
            <i class="fas fa-download pr-1" />
            Pobierz raport
          </button>
        </div>
      </div>
    </BModal>

    <BModal
      v-model="form"
      hide-footer
      hide-header
      size="lg"
    >
      <ReportSendSettingsForm
        :subscription="edited"
        @close="form = false"
        @updated="subscriptions = $event"
      />
    </BModal>
  </div>
</template>

<script>
import ReportSendSettingsForm from '@/components/shop/report/ReportSendSettingsForm';
import getFileName from '@/utils/file-name';
import moment from 'moment';
import CtkDateTimePicker from 'vue-ctk-date-time-picker';
import { mapActions, mapGetters } from 'vuex';
import SubscriptionRow from './subscription/SubscriptionRow';

export default {
  props: {
    catalogId: String,
  },
  data: () => ({
    modal: false,
    form: false,
    days: [],
    dates: null,
    subscriptions: [],
    pending: {},
    edited: null,
    reportDates: {
      start: moment().format('YYYY-MM-DD'),
      end: moment().format('YYYY-MM-DD'),
    },
    sendPending: false,
    downloadPending: false,
    selectedReport: null,
  }),
  components: {
    ReportSendSettingsForm,
    SubscriptionRow,
    CtkDateTimePicker,
  },
  computed: {
    ...mapGetters([
      'locale',
      'payee',
      'userGroups',
      'properties',
    ]),
    ...mapGetters('shop', [
      'ordersReportTypes',
      'singleOrderSchema',
    ]),
    customShortcuts() {
      return [
        { key: 'today', label: 'Dzisiaj', value: 'day' },
        { key: 'lastWeek', label: 'Poprzedni tydzień', value: '-isoWeek' },
        { key: 'last30Days', label: 'Ostatnie 30 dni', value: 30 },
        { key: 'thisMonth', label: 'Ten miesiąc', value: 'month' },
        { key: 'lastMonth', label: 'Poprzedni miesiąc', value: '-month' },
      ];
    },
    parsedSubscriptions() {
      return this.subscriptions
        .map((x) => ({
          ...x,
          config: JSON.parse(x.config),
          reportType: this.ordersReportTypes.find((t) => t.type === x.type),
        }))
        .map((x) => ({
          ...x,
          groups: x.config.groups
            .map((code) => {
              if (code === '#all') {
                return this.$t('company.all');
              }
              return this.userGroups.find((y) => y.code === code)?.path || '?';
            }),
        }))
        .sort((a, b) => (a.config?.name || '').localeCompare(b.config?.name || ''));
    },
  },
  methods: {
    ...mapActions('shop', [
      'getReportSubscriptions',
      'toggleReportSubscription',
    ]),
    sendReport(id) {
      if (this.pending[id]) {
        return;
      }

      this.$set(this.pending, id, 'email');

      this.toggleReportSubscription({
        params: {
          id,
          query: {
            send: true,
            catalogId: this.catalogId,
            from: this.reportDates.start,
            to: this.reportDates.end || this.reportDates.start,
          },
        },
      })
        .finally(() => {
          this.$set(this.pending, id, undefined);
        });
    },
    downloadReport(id) {
      if (this.pending[id]) {
        return;
      }

      this.$set(this.pending, id, 'download');

      this.toggleReportSubscription({
        params: {
          id,
          query: {
            send: false,
            catalogId: this.catalogId,
            from: this.reportDates.start,
            to: this.reportDates.end || this.reportDates.start,
          },
        },
      })
        .then(({ data, headers }) => {
          const name = getFileName(headers);
          const url = window.URL.createObjectURL(new Blob([data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', name);
          document.body.appendChild(link);
          link.click();
        })
        .finally(() => {
          this.$set(this.pending, id, undefined);
        });
    },
    request() {
      this.getReportSubscriptions({
        params: {
          query: {
            catalogId: this.catalogId,
          },
        },
      })
        .then(({ data }) => {
          this.subscriptions = data;
        });
    },
  },
  created() {
    this.request();
    this.$emit('page', 'reports');
  },
};
</script>

<style lang="scss" scoped>

.date-picker :deep(.custom-button-content) {
  text-wrap: nowrap;
}

.subscription-scroll {
  height: calc(100vh - 275px);
  overflow: auto;
  padding-bottom: 120px;
}

.secondary-icon.disabled {
  opacity: 0.5;
}
</style>
